import * as React from "react";
import { useCallback, useContext, useMemo } from "react";
import * as Immutable from "immutable";
import styled from "styled-components";
import { AdditionalContext } from "views/logic/ActionContext";
import StreamsContext from "contexts/StreamsContext";
import FieldType from "views/logic/fieldtypes/FieldType";
import { MESSAGE_FIELD } from "views/Constants";
import useInputsList from "hooks/useInputs";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { TableDataCell } from "views/components/datatable";
import MessageDetail from "components/common/message/details/MessageDetail";
import DecoratedValue from "views/components/messagelist/decoration/DecoratedValue";
import CustomHighlighting from "views/components/highlighting/CustomHighlighting";
import TypeSpecificValue from "views/components/TypeSpecificValue";
import HighlightMessageContext from "views/components/contexts/HighlightMessageContext";
import { toSelectableMessageTableEntry } from "views/components/widgets/MessageTableSelectedEntitiesProvider";
import RowCheckbox from "components/common/EntityDataTable/RowCheckbox";
import BulkSelectCell from "components/common/message/messagetable/BulkSelectCell";
import useSelectedMessageEntities from "views/hooks/useSelectedMessageEntities";
import MessageTableOverrideRow from "./MessageTableOverrideRow";
import MessagePreview from "./MessagePreview";
export const TableBody = styled.tbody(
  ({ $expanded, $highlighted, theme }) => `
  && {
    border-top: 0;

    ${$expanded ? `
  border-left: 7px solid ${theme.colors.variant.light.info};
` : ""}

    ${$highlighted ? `
  border-left: 7px solid ${theme.colors.variant.light.success};
` : ""}
  }
`
);
const FieldsRow = styled.tr`
  cursor: ${({ $clickable }) => $clickable ? "pointer" : "default"};

  && td {
    min-width: 50px;
    word-break: break-word;
    padding: 4px 5px 2px;
  }

  time {
    line-height: 1;
  }
`;
const ActionsCell = styled(TableDataCell)`
  width: 1%;
  white-space: nowrap;
  text-align: right;
`;
const MessageDetailRow = styled.tr`
  td {
    padding-top: 5px;
    border-top: 0;
  }

  .row {
    margin-right: 0;
  }

  div[class*='col-'] {
    padding-right: 0;
  }
`;
const isDecoratedField = (field, decorationStats) => decorationStats && (decorationStats.added_fields[field] !== void 0 || decorationStats.changed_fields[field] !== void 0);
const fieldType = (fieldName, { decoration_stats: decorationStats }, fields) => {
  var _a;
  return isDecoratedField(fieldName, decorationStats) ? FieldType.Decorated : ((_a = fields == null ? void 0 : fields.find((f) => f.name === fieldName)) != null ? _a : { type: FieldType.Unknown }).type;
};
const Strong = ({ children = void 0, strong }) => strong ? /* @__PURE__ */ React.createElement("strong", null, children) : /* @__PURE__ */ React.createElement(React.Fragment, null, children);
const RowBulkCheckbox = ({
  message,
  isEntitySelectable = () => false
}) => {
  const { toggleEntitySelect, isEntitySelected } = useSelectedMessageEntities();
  const isSelected = isEntitySelected(message.index, message.id);
  const checkboxTitle = `${isSelected ? "Deselect" : "Select"} message`;
  const isSelectDisabled = !isEntitySelectable(toSelectableMessageTableEntry(message));
  return /* @__PURE__ */ React.createElement(BulkSelectCell, null, /* @__PURE__ */ React.createElement(
    RowCheckbox,
    {
      onChange: () => toggleEntitySelect(message.index, message.id),
      title: !isSelectDisabled ? checkboxTitle : void 0,
      checked: isSelected,
      disabled: isSelectDisabled,
      "aria-label": checkboxTitle
    }
  ));
};
const MessageTableEntry = ({
  config,
  disableSurroundingSearch = false,
  expandAllRenderAsync,
  expanded,
  fields,
  message,
  rowActions = void 0,
  showMessageRow = false,
  selectedFields = Immutable.OrderedSet(),
  toggleDetail,
  displayBulkSelectCol = false,
  isEntitySelectable = () => false,
  overrideContent = void 0
}) => {
  const { data: inputsList = [] } = useInputsList();
  const streamsList = useContext(StreamsContext);
  const highlightMessageId = useContext(HighlightMessageContext);
  const sendTelemetry = useSendTelemetry();
  const additionalContextValue = useMemo(() => ({ message }), [message]);
  const streams = useMemo(
    () => Immutable.Map((streamsList != null ? streamsList : []).map((stream) => [stream.id, stream])),
    [streamsList]
  );
  const inputs = useMemo(
    () => Immutable.Map(inputsList.map((input) => [input.id, input])),
    [inputsList]
  );
  const _toggleDetail = useCallback(() => {
    var _a;
    if (overrideContent) {
      return;
    }
    const isSelectingText = !!((_a = window.getSelection()) == null ? void 0 : _a.toString());
    if (!isSelectingText) {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_MESSAGE_TABLE_DETAILS_TOGGLED, {
        app_section: "widget",
        app_action_value: "widget-message-table-toggle-details"
      });
      toggleDetail(`${message.index}-${message.id}`);
    }
  }, [overrideContent, message.id, message.index, sendTelemetry, toggleDetail]);
  const colSpanFixup = selectedFields.size + 1 + (rowActions ? 1 : 0) + (displayBulkSelectCol ? 1 : 0);
  const selectedFieldsList = useMemo(
    () => selectedFields.toArray().map((selectedFieldName, idx) => {
      const type = fieldType(selectedFieldName, message, fields);
      return /* @__PURE__ */ React.createElement(
        TableDataCell,
        {
          className: "table-data-cell",
          $isNumeric: type.isNumeric(),
          key: selectedFieldName,
          "data-testid": `message-summary-field-${selectedFieldName}`
        },
        /* @__PURE__ */ React.createElement(Strong, { strong: idx === 0 }, /* @__PURE__ */ React.createElement(CustomHighlighting, { field: selectedFieldName, value: message.fields[selectedFieldName] }, /* @__PURE__ */ React.createElement(
          TypeSpecificValue,
          {
            value: message.fields[selectedFieldName],
            field: selectedFieldName,
            type,
            render: DecoratedValue
          }
        )))
      );
    }),
    [fields, message, selectedFields]
  );
  const messageFieldType = useMemo(() => fieldType(MESSAGE_FIELD, message, fields), [fields, message]);
  return /* @__PURE__ */ React.createElement(AdditionalContext.Provider, { value: additionalContextValue }, /* @__PURE__ */ React.createElement(TableBody, { $expanded: expanded, $highlighted: message.id === highlightMessageId }, overrideContent ? /* @__PURE__ */ React.createElement(MessageTableOverrideRow, { colSpan: colSpanFixup, notice: overrideContent, rowActions }) : /* @__PURE__ */ React.createElement(FieldsRow, { onClick: _toggleDetail, className: "table-data-row", $clickable: true }, displayBulkSelectCol && /* @__PURE__ */ React.createElement(RowBulkCheckbox, { message, isEntitySelectable }), selectedFieldsList, rowActions && /* @__PURE__ */ React.createElement(ActionsCell, { $isNumeric: false }, rowActions)), !overrideContent && /* @__PURE__ */ React.createElement(
    MessagePreview,
    {
      showMessageRow,
      config,
      colSpanFixup,
      messageFieldType,
      onRowClick: _toggleDetail,
      message,
      displayBulkSelectCol
    }
  ), expanded && !overrideContent && /* @__PURE__ */ React.createElement(MessageDetailRow, null, /* @__PURE__ */ React.createElement("td", { colSpan: colSpanFixup }, /* @__PURE__ */ React.createElement(
    MessageDetail,
    {
      message,
      fields,
      streams,
      inputs,
      disableSurroundingSearch,
      expandAllRenderAsync
    }
  )))));
};
export default MessageTableEntry;
